if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.onPopState = this.onPopState.bind(this);
      }

      show(opener) {
        super.show(opener);
        this.setupNavigation();
        this.showActiveMedia();
        this.updateNavigation();
        this.pushHistoryEntry();
      }

      // Every close path inherited from ModalDialog lands here: the X button,
      // the Escape key and the desktop pointerup handler. Unwind the entry we
      // pushed so the browser's back stack stays in step with what's on screen.
      hide() {
        if (this.historyEntryActive) {
          this.releaseHistoryEntry();
          super.hide();
          history.back();
          return;
        }
        super.hide();
      }

      // The lightbox is an overlay, not a page, so the browser has no entry to
      // go back to. Without one, the phone's back button — the natural gesture
      // for dismissing a fullscreen image — navigates off the product page
      // entirely, landing the customer back on the collection. Pushing a
      // throwaway entry makes back close the lightbox and leave the product
      // page exactly where it was.
      pushHistoryEntry() {
        if (this.historyEntryActive) return;
        this.historyEntryActive = true;
        // No URL argument: the address bar must not change, only the stack depth.
        history.pushState({ productLightbox: this.id }, '');
        window.addEventListener('popstate', this.onPopState);
      }

      releaseHistoryEntry() {
        if (!this.historyEntryActive) return;
        this.historyEntryActive = false;
        window.removeEventListener('popstate', this.onPopState);
      }

      // Back pressed while open: our entry is already gone, so close the
      // overlay without touching history again.
      onPopState() {
        this.releaseHistoryEntry();
        super.hide();
      }

      get slideTrack() {
        return this.querySelector('[role="document"]');
      }

      // Variant duplicates are hidden with display:none by the stylesheet, so
      // they must not count as slides.
      get slides() {
        if (!this.slideTrack) return [];
        return Array.from(this.slideTrack.children).filter(
          (slide) => getComputedStyle(slide).display !== 'none'
        );
      }

      setupNavigation() {
        if (this.navigationReady) return;
        this.prevButton = this.querySelector('[data-lightbox-prev]');
        this.nextButton = this.querySelector('[data-lightbox-next]');
        if (!this.prevButton || !this.nextButton || !this.slideTrack) return;
        this.navigationReady = true;

        [this.prevButton, this.nextButton].forEach((button) => {
          // ModalDialog closes a .media-modal on any mouse pointerup that
          // reaches it, which would dismiss the lightbox the instant an arrow
          // is clicked. Keep the event from bubbling that far.
          button.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.prevButton.addEventListener('click', () => this.step(-1));
        this.nextButton.addEventListener('click', () => this.step(1));

        this.slideTrack.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = requestAnimationFrame(() => this.updateNavigation());
          },
          { passive: true }
        );
      }

      // Derived from geometry rather than scrollLeft, whose sign and origin
      // under RTL differ between engines.
      currentIndex() {
        const slides = this.slides;
        if (slides.length === 0) return 0;
        const track = this.slideTrack.getBoundingClientRect();
        const centre = track.left + track.width / 2;
        let closest = 0;
        let smallest = Infinity;
        slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - centre);
          if (distance < smallest) {
            smallest = distance;
            closest = index;
          }
        });
        return closest;
      }

      step(delta) {
        const slides = this.slides;
        if (slides.length === 0) return;
        const target = Math.min(Math.max(this.currentIndex() + delta, 0), slides.length - 1);
        slides[target].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }

      updateNavigation() {
        if (!this.navigationReady) return;
        const slides = this.slides;
        const single = slides.length <= 1;
        this.prevButton.hidden = single;
        this.nextButton.hidden = single;
        if (single) return;
        const index = this.currentIndex();
        this.prevButton.disabled = index === 0;
        this.nextButton.disabled = index === slides.length - 1;
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        // Centre the clicked image in the track without animating the jump, and
        // without dragging the page behind the overlay along with it.
        activeMedia.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
